"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  value: 35,
  suffix: "%",
  label: "REVENUE GROWTH",
  description: "Average increase in RevPAR for our partner properties within 6 months."
}, {
  value: 98,
  suffix: "%",
  label: "RATE PARITY",
  description: "Ensuring consistent pricing across all OTA and direct channels."
}, {
  value: 24,
  suffix: "/7",
  label: "REVENUE MONITORING",
  description: "Continuous data-driven adjustments to maximize your daily yield."
}, {
  value: 0,
  suffix: "",
  label: "HIDDEN FEES",
  description: "Transparent, performance-based consulting with no hidden costs."
}];
function AnimatedNumber({
  target,
  isVisible
}: {
  target: number;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    if (target === 0) {
      setCount(0);
      return;
    }
    const duration = 1500;
    const startTime = performance.now();
    let frame: number;
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * eased));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, isVisible]);
  return <>{count}</>;
}
export default function Performance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="4372" id="performanceanalyticsdashboard" className="relative bg-background text-foreground py-24 md:py-40 overflow-hidden">
      {}
      <div className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2 z-10">
        <div className="font-default text-xs tracking-[0.3em] uppercase text-secondary whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          FIG. 01 — IMPACT
        </div>
      </div>

      {}
      <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 z-10 opacity-70">
        <svg width="80" height="220" viewBox="0 0 80 220" fill="none" stroke="var(--primary)" strokeWidth="1" xmlns="http://www.w3.org/2000/svg">
          <line x1="40" y1="0" x2="40" y2="60" />
          <circle cx="40" cy="60" r="3" />
          <line x1="40" y1="60" x2="40" y2="100" />
          <line x1="40" y1="75" x2="20" y2="90" />
          <line x1="40" y1="75" x2="60" y2="90" />
          <line x1="40" y1="100" x2="15" y2="135" />
          <line x1="40" y1="100" x2="65" y2="135" />
          <line x1="40" y1="100" x2="40" y2="150" />
          <line x1="15" y1="135" x2="5" y2="165" />
          <line x1="15" y1="135" x2="25" y2="170" />
          <line x1="65" y1="135" x2="55" y2="170" />
          <line x1="65" y1="135" x2="75" y2="165" />
          <line x1="40" y1="150" x2="30" y2="185" />
          <line x1="40" y1="150" x2="50" y2="185" />
          <line x1="40" y1="150" x2="40" y2="200" />
          <line x1="5" y1="165" x2="2" y2="195" />
          <line x1="25" y1="170" x2="22" y2="200" />
          <line x1="55" y1="170" x2="58" y2="200" />
          <line x1="75" y1="165" x2="78" y2="195" />
        </svg>
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-12">
        {}
        <div className="text-center mb-8">
          <span className="font-default text-xs tracking-[0.3em] uppercase text-primary">
            RESULTS — VOL. 01
          </span>
        </div>

        {}
        <h2 className="font-default text-center text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-20 md:mb-28">
          Revenue, Optimized.
        </h2>

        {}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-16 lg:gap-y-0">
          {stats.map((stat, i) => <div data-index={i} key={i} className={`relative px-6 md:px-8 py-12 md:py-24 text-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${i > 0 ? "lg:border-l border-border" : ""} ${i % 2 === 1 ? "border-l border-border lg:border-l" : ""} ${i >= 2 ? "border-t border-border lg:border-t-0" : ""}`} style={{
          transitionDelay: `${i * 120}ms`
        }}>
              {}
              <div className="font-default text-6xl md:text-7xl lg:text-8xl font-bold text-primary leading-none mb-6 md:mb-8">
                <AnimatedNumber target={stat.value} isVisible={isVisible} />
                <span className="font-bold text-foreground/80 text-5xl md:text-6xl lg:text-7xl ml-1">
                  {stat.suffix}
                </span>
              </div>

              {}
              <div className="font-default text-[10px] md:text-xs tracking-[0.25em] uppercase text-secondary mb-4">
                {stat.label}
              </div>

              {}
              <p className="text-xs md:text-sm text-foreground/70 max-w-[200px] mx-auto leading-relaxed">
                {stat.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}